import { ColumnKind } from './columns';

/** Case-insensitive strings a pasted cell treats as a checked box. */
const TRUTHY_STRINGS = new Set(['true', '1', 'yes', 'y', 'x']);

const NO_KEYS: ReadonlySet<string> = new Set();

/** Split Excel clipboard TSV into a 2-D array of raw cell strings. */
export function parseClipboardTable(text: string): string[][] {
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalized.split('\n');
    if (lines.length > 1 && lines[lines.length - 1] === '') {
        // Excel terminates a copied range with a trailing newline, which is dropped, but a trailing empty cell is real and kept.
        lines.pop();
    }
    return lines.map((line) => line.split('\t'));
}

/**
 * Coerce one raw pasted string into the value shape `columnKinds.get(columnKey)` expects — a lookup miss defaults to `'text'`.
 * A blank cell in a `nullableBoolKeys` column (a custom boolean, which can be unset) is `null`, not `false`.
 */
export function coerceCell(
    raw: string,
    columnKey: string,
    columnKinds: ReadonlyMap<string, ColumnKind>,
    nullableBoolKeys: ReadonlySet<string> = NO_KEYS,
): unknown {
    const trimmed = raw.trim();
    switch (columnKinds.get(columnKey) ?? 'text') {
        case 'bool':
            if (trimmed === '' && nullableBoolKeys.has(columnKey)) return null;
            return TRUTHY_STRINGS.has(trimmed.toLowerCase());
        case 'number': {
            if (trimmed === '') return null;
            // Unparseable numeric paste is dropped, not stored as NaN.
            const n = Number(trimmed);
            return Number.isNaN(n) ? null : n;
        }
        case 'integer': {
            if (trimmed === '') return null;
            // Unlike `number`, a non-integer paste (1.5) is kept so validation can name it.
            const n = Number(trimmed);
            return Number.isNaN(n) ? null : n;
        }
        case 'decimal':
            // Kept as the string pasted: a Decimal must not round-trip through a float.
            return trimmed === '' || Number.isNaN(Number(trimmed))
                ? null
                : trimmed;
        case 'enum':
            return trimmed === '' ? null : trimmed;
        case 'list':
            return trimmed === ''
                ? []
                : trimmed
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
        case 'text':
        case 'long_text':
        case 'date':
        default:
            // No trim here — matches `CellInput`'s text `onChange`, which
            // preserves whitespace exactly as typed (`v === '' ? null : v`).
            return raw === '' ? null : raw;
    }
}

/**
 * Render a draft's current cell value back to the raw string `coerceCell` would produce it from — the inverse of {@link coerceCell}.
 * A `nullableBool` cell keeps an explicit `false` as `'false'`, so it stays apart from unset.
 */
export function cellValueToRaw(
    value: unknown,
    kind: ColumnKind,
    nullableBool = false,
): string {
    switch (kind) {
        case 'bool':
            if (value === true) return 'true';
            return nullableBool && value === false ? 'false' : '';
        case 'list':
            return Array.isArray(value) ? value.join(', ') : '';
        case 'number':
        case 'integer':
        case 'decimal':
        case 'enum':
        case 'text':
        case 'long_text':
        case 'date':
        default:
            return value == null ? '' : String(value);
    }
}
